import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { AreaType } from "../interfaces/IAreaType";
import { ICalcInput, ICalcResult, ICheckInput, ICheckResult } from "../interfaces/ICalculations";
import { PrintoutType } from "../models/boms/bom.model";
import { LedPatterns } from "../models/leds-patterns";
import { ProductList, ProductParameters } from "../models/product-parameters.model";
import { FormSideParameter, WizardParameter } from "../models/wizard/wizard-parameter.model";
import { Color } from '../models/colors/color.model';
import { map, shareReplay } from "rxjs/operators";
import { IMigrationInput, IMigrationResult } from "../interfaces/IStorageCommon";
import { ISysInfo } from "../interfaces/IProjectInfo";
import { CRMImportResponse, GetOfferResponse, ProjectData } from "../project-data";
import { ICRMExportResult } from "../interfaces/ICRMIntegration";

@Injectable({
	providedIn: 'root'
})
export class ApiService {
	
	constructor(
		private http: HttpClient,
		@Inject('BASE_URL') private baseUrl: string) {
	}

	public getSysInfo(): Observable<ISysInfo> {
		return this.http.get<ISysInfo>(this.baseUrl + `/api/application/sysinfo`);
	}

	public getToolbox(productId: string, brandName: string, area: AreaType): Observable<any> {
		return this.http.get<any>(`${this.baseUrl}/api/toolbox/${productId}/${brandName}/${area}`);
	}

	public getLedsPatterns(productId: string, brandName: string): Observable<LedPatterns> {
		return this.http.get<LedPatterns>(`${this.baseUrl}/api/product/${productId}/${brandName}/ledspots`);
	}   

	public getGlasses() {
		return this.http.get<any>(this.baseUrl + '/api/glasses/get');
	}

	public calculateConstruction(productId: string, brandName: string, input: ICalcInput): Observable<ICalcResult> {
		return this.http.post<ICalcResult>(`${this.baseUrl}/api/construction/${productId}/${brandName}/calc`, input);
	}

	public calculateColumnAndGlassLength(productId: string, brandName: string, input: ICheckInput): Observable<any> {
		return this.http.post<any>(`${this.baseUrl}/api/construction/${productId}/${brandName}/colglasslength`, input);
	}

	public checkConstruction(productId: string, brandName: string, input: ICheckInput): Observable<ICheckResult> {
		return this.http.post<ICheckResult>(`${this.baseUrl}/api/construction/${productId}/${brandName}/check`, input);
	}

	public sendBom(productId: string, bom: any, brandName: string): Observable<any> {
		return this.http.post<any>(`${this.baseUrl}/api/project/${productId}/${brandName}/bom`, bom);
	}

	public exportToCRM(productId: string, project: ProjectData, brandName: string): Observable<ICRMExportResult> {
		return this.http.post<ICRMExportResult>(`${this.baseUrl}/api/project/crmexport`, project);
	}

	public getCRMProject(id: string): Observable<CRMImportResponse<GetOfferResponse>> {
		return this.http.get<CRMImportResponse<GetOfferResponse>>(`${this.baseUrl}/api/project/crmimport/${id}`);
	}

	public getExcel(productId: string, brandName: string, bom: any): Observable<Blob> {
		const lang = window.document.documentElement.lang ?? 'en'
		const httpOptions = {
			headers: new HttpHeaders({ 'Accept-Language': lang }),
			responseType: 'blob' as 'json'
		}

		return this.http.post<Blob>(`${this.baseUrl}/api/project/${productId}/${brandName}/excel`, bom, httpOptions );
	}

	public sendImage(orderCode: string, type: PrintoutType, data: string, id: string): Observable<any> {
		const httpOptions = {
			headers: new HttpHeaders({ 'Content-Type': 'text/plain' })
		}

		if (!id) {
			id = "-";
		}
		return this.http.post<any>(this.baseUrl + `/api/project/saveimage/${orderCode}/${type}/${id}`, data, httpOptions);
	}

	//#region Wizard

	public getProducts(): Observable<ProductList> {
		return this.http.get<ProductList>(`${this.baseUrl}/api/products`);
	}

	public getProductParameters(productId: string, brandName: string) : Observable<ProductParameters>{
		return this.http.get<ProductParameters>(`${this.baseUrl}/api/product/${productId}/${brandName}/parameters`);
	}

	public  getFormTops(productId: string, brandName: string): Observable<WizardParameter[]> {
		return this.http.get<WizardParameter[]>(`${this.baseUrl}/api/product/${productId}/${brandName}/formtops`);
	}

	public getFoundations(productId: string, brandName: string): Observable<WizardParameter[]> {
		return this.http.get<WizardParameter[]>(`${this.baseUrl}/api/product/${productId}/${brandName}/foundations`);
	}

	public getFormSides(productId: string, brandName: string): Observable<FormSideParameter[]> {
		return this.http.get<FormSideParameter[]>(`${this.baseUrl}/api/product/${productId}/${brandName}/formsides`);
	}

	public getRoofings(productId: string, brandName: string): Observable<WizardParameter[]> {
		return this.http.get<WizardParameter[]>(`${this.baseUrl}/api/product/${productId}/${brandName}/roofings`);
	}

	public getClimateZones(postalCode: string): Observable<{ snowZone: string, windZone: string }> {
		return this.http.get<{ snowZone: string, windZone: string }>(`${this.baseUrl}/api/climatezones/${postalCode}`);
	}

	public getFastenings(productId: string, brandName: string): Observable<WizardParameter[]> {
		return this.http.get<WizardParameter[]>(`${this.baseUrl}/api/product/${productId}/${brandName}/fastenings`);
	}

	public getElements(productId: string, brandName: string): Observable<any> {
		return this.http.get<any>(`${this.baseUrl}/api/product/${productId}/${brandName}/elements`);
	}

	//#endregion

	public getColors(productId: string, brandName: string): Observable<any> {
		return this.http.get<Color[]>(`${this.baseUrl}/api/product/${productId}/${brandName}/colors`);
	}

	public getFullColors(productId: string, brandName: string): Observable<any> {
		return this.http.get<Color[]>(`${this.baseUrl}/api/product/${productId}/${brandName}/fullcolors`);
	}

	public getColorsWithPalette(colors: Color[], productId: string, brandName: string) {
		const paletteCache = new Map<string, Observable<any>>();
		return colors.map((color) => {
			if (!color.paletteId) {
				return of(color);
			}
			if (!paletteCache.has(color.paletteId)) {
				paletteCache.set(
					color.paletteId,
					this.getPaletteGroups(productId, color.paletteId, brandName).pipe(shareReplay(1))
				);
			}
			return paletteCache.get(color.paletteId)!.pipe(
				map((paletteGroups) => {
					color.paletteGroups = paletteGroups;
					return color;
				})
			);
		});
	}

	private getPaletteGroups(productId: string, paletteId: string, brandName: string,): Observable<any> {
		return this.http.get<any>(`${this.baseUrl}/api/product/${productId}/${paletteId}/${brandName}/palettegroups`);
	}

	public login(password: string): Observable<any> {
		const httpOptions = {
			headers: new HttpHeaders({ 'Content-Type': 'text/plain' })
		}
		return this.http.post<boolean>(this.baseUrl + '/api/auth/login/', password, httpOptions);
	}

	public checkMigration(input: IMigrationInput): Observable<IMigrationResult> {
		return this.http.post<IMigrationResult>(this.baseUrl + '/api/construction/checkmigration', input);
	}

}
